// apps/web/src/auth/gis-client.ts
// Hand-rolled minimal types for Google Identity Services (GIS) gsi/client
// + postCredential() helper to POST the GIS-returned ID token to
// api-worker's /api/auth/google endpoint (which mints the session cookie).
//
// Per Plan §4.7.4 architecture diagram (lines 4297-4332) +
// workers/api-worker/src/middleware/auth.ts:57 (expected body shape).

// ─── GIS global types ──────────────────────────────────────────────────

export interface CredentialResponse {
  credential: string;
  select_by?: string;
  clientId?: string;
}

interface IdConfiguration {
  client_id: string;
  callback: (response: CredentialResponse) => void;
  auto_select?: boolean;
  cancel_on_tap_outside?: boolean;
}

interface GsiButtonOptions {
  type?:            'standard' | 'icon';
  theme?:           'outline' | 'filled_blue' | 'filled_black';
  size?:            'small' | 'medium' | 'large';
  text?:            'signin_with' | 'signup_with' | 'continue_with' | 'signin';
  shape?:           'rectangular' | 'pill' | 'circle' | 'square';
  logo_alignment?:  'left' | 'center';
  width?:           number | string;
  locale?:          string;
}

interface GoogleAccountsId {
  initialize:        (config: IdConfiguration) => void;
  renderButton:      (parent: HTMLElement, options: GsiButtonOptions) => void;
  prompt:            () => void;
  disableAutoSelect: () => void;
  cancel:            () => void;
}

declare global {
  interface Window {
    google?: {
      accounts: {
        id: GoogleAccountsId;
      };
    };
  }
}

// ─── POST /api/auth/google round-trip ──────────────────────────────────

// Empty default = relative path = same-origin via Pages Function proxy
// (Step 22.E-prime-2 2026-05-18 — first-party cookie posture).
const API_BASE = import.meta.env.VITE_API_URL ?? '';

interface LoginSuccess {
  ok:    true;
  email: string;
}

interface LoginFailure {
  error: string;
  code:  'AUTH_MISSING' | 'AUTH_INVALID' | 'AUTH_FORBIDDEN';
}

/**
 * POSTs the GIS credential to api-worker. On success the server sets the
 * HttpOnly session cookie and returns the user's email. credentials:'include'
 * is mandatory so the browser stores the response Set-Cookie under api-worker
 * domain (cross-site cookie — Plan §4.7.10 L-3 + §4.7.4 architecture).
 *
 * Throws on non-2xx with the server's `error` field for UI display.
 */
export async function postCredential(credential: string): Promise<LoginSuccess> {
  const res = await fetch(`${API_BASE}/api/auth/google`, {
    method:      'POST',
    headers:     { 'Content-Type': 'application/json' },
    body:        JSON.stringify({ credential }),
    credentials: 'include',
  });

  const body = (await res.json().catch(() => ({}))) as LoginSuccess | LoginFailure;

  if (!res.ok) {
    const err = body as LoginFailure;
    throw new Error(err.error || `Login failed (HTTP ${res.status})`);
  }

  return body as LoginSuccess;
}
